import { useFormContext, Controller } from "react-hook-form";
import { useIntl } from "react-intl";

import useFeatureFlag from "hooks/useFeatureFlag";
import { FEATURE_FLAGS } from "constants/featureFlags";
import { StopAddressType } from "types/MultiStop";
import testIds from "constants/testIds";
import { multiStopPickupDeliveryOption } from "../../ScheduleDefaultOptions";

export default function StopAddressTypeForm({ index }: { index: number }) {
  const intl = useIntl();

  const { control, setValue, clearErrors } = useFormContext();
  const isPortAddressV2Enabled = useFeatureFlag(FEATURE_FLAGS.FF_WEB10382_OPTIONAL_PORT_ADDRESS_FORM);

  const fieldName = `schedule.stops.${index}.stop_address_type`;
  const options = multiStopPickupDeliveryOption(intl);

  // When the WEB-10382 port-linked address flow is active, clear the stop's
  // previously selected address + address_id / reference_number when the user
  // switches types. Values are repopulated from the next port selection
  // (auto-fill) or a manual address choice.
  const clearStopAddressFields = () => {
    setValue(`schedule.stops.${index}.address_id`, undefined);
    setValue(`schedule.stops.${index}.address_reference_number`, "");
    setValue(`schedule.stops.${index}.address_name`, "", { shouldValidate: false });
    setValue(`schedule.stops.${index}.street_address`, "");
    setValue(`schedule.stops.${index}.street_address_line_two`, "");
    setValue(`schedule.stops.${index}.city_name`, "");
    setValue(`schedule.stops.${index}.state_name`, "");
    setValue(`schedule.stops.${index}.country_name`, "");
    setValue(`schedule.stops.${index}.port_country`, "");
    setValue(`schedule.stops.${index}.zipcode`, "");
    setValue(`schedule.stops.${index}.stop_timezone`, "", { shouldValidate: false });
  };

  const handleChangeAddressType = (newAddressType: StopAddressType, onChange: Function) => {
    onChange(newAddressType);
    const stopAddressField = `schedule.stops.${index}.address_name`;
    const stopTimezoneField = `schedule.stops.${index}.stop_timezone`;
    setValue(`schedule.stops.${index}.port_country`, "");

    if (newAddressType === StopAddressType.ADDRESS) {
      setValue(`schedule.stops.${index}.stop_airport_name`, "");
      setValue(`schedule.stops.${index}.stop_airport_code`, "");
      setValue(`schedule.stops.${index}.stop_seaport_name`, "");
      setValue(`schedule.stops.${index}.stop_seaport_code`, "");
    }
    if (newAddressType === StopAddressType.SEAPORT) {
      setValue(`schedule.stops.${index}.stop_airport_name`, "");
      setValue(`schedule.stops.${index}.stop_airport_code`, "");
    }
    if (newAddressType === StopAddressType.AIRPORT) {
      setValue(`schedule.stops.${index}.stop_seaport_name`, "");
      setValue(`schedule.stops.${index}.stop_seaport_code`, "");
    }

    if (isPortAddressV2Enabled) {
      clearStopAddressFields();
    }

    // Address type toggles can leave stale required errors from previous mode.
    // Always clear stop address + timezone errors and reset values without
    // immediate revalidation so the form returns to neutral state.
    clearErrors([stopAddressField, stopTimezoneField]);
    setValue(stopAddressField, "", { shouldValidate: false });
    setValue(stopTimezoneField, "", { shouldValidate: false });
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field }) => (
        <div className="d-flex items-center gap-xl mb-xl">
          {options.map((option) => {
            const id = `${fieldName}-${option.value}`;
            const isChecked = String(field.value) === String(option.value);

            return (
              <label
                key={option.value}
                htmlFor={id}
                className="font-weight-normal position-relative d-flex gap-sm align-items-center cursor-pointer"
              >
                {/* Hidden native input for RHF */}
                <input
                  id={id}
                  type="radio"
                  data-testid={`${testIds.REQUEST_FORM_SCHEDULE_MULTISTOP_STOP_ADDRESS_TYPE_PREFIX}-${index}-${option.value}`}
                  value={option.value}
                  checked={isChecked}
                  className="sr-only"
                  onChange={() => handleChangeAddressType(option.value, field.onChange)}
                />

                {/* Custom styled circle */}
                <span className="checkmark" />
                <span className="mb-0">{option.label}</span>
              </label>
            );
          })}
        </div>
      )}
    />
  );
}
